import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiDownload, FiSearch, FiTrendingUp, FiUsers, FiX } from "react-icons/fi";
import agentReportsService from "../../api/services/agentReportsService";
import { ClientCallSummaryTable } from "../../components/CallRecordsTable";
import { CardsSkeleton, TableSkeleton } from "../../components/common/skeletons";
import PaginationBar from "../../components/common/PaginationBar";
import useClientPagination from "../../hooks/useClientPagination";
import { downloadCsv } from "../../utils/downloadCsv";

function todayKey() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const THEME = { primary: "#FB565F" };
const REPORTS_PAGE_SIZE = 20;
const inputCls =
  "border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-400 outline-none";

const ALLOWED = [
  "Admin",
  "Developer",
  "Sales Team Lead",
  "CSR Team Lead",
  "CSR",
  "Sales",
  "Chat Support",
];

const ROLE_STYLES = {
  CSR: "bg-teal-100 text-teal-800",
  "CSR Team Lead": "bg-indigo-100 text-indigo-800",
  "Chat Support": "bg-cyan-100 text-cyan-800",
  Sales: "bg-amber-100 text-amber-800",
  "Sales Team Lead": "bg-orange-100 text-orange-800",
};

function callMinutes(sec) {
  return Math.round((Number(sec) || 0) / 60);
}

function RoleBadge({ role }) {
  if (!role) return <span className="text-xs text-gray-400">—</span>;
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
        ROLE_STYLES[role] || "bg-gray-100 text-gray-600"
      }`}
    >
      {role}
    </span>
  );
}

const TICKET_STATUS_COLUMNS = [
  { key: "PENDING", label: "Pending" },
  { key: "IN_PROGRESS", label: "In progress" },
  { key: "COMPLETED", label: "Completed" },
  { key: "RESOLVED", label: "Resolved" },
  { key: "CLOSED", label: "Closed" },
];

function isChatSupportTeamType(teamType, panelRole) {
  if (teamType === "chat_support") return true;
  if (teamType === "csr") return false;
  return panelRole === "Chat Support";
}

function PerformanceTable({ rows, expandedAgentId, onToggleExpand, teamType = null }) {
  const showTicketBreakdown = rows.some((r) => isChatSupportTeamType(teamType || r.teamType, r.panelRole));
  const showTicketsColumn = showTicketBreakdown;
  const {
    pageItems,
    page,
    setPage,
    totalItems,
    totalPages,
    pageSize,
  } = useClientPagination(rows, REPORTS_PAGE_SIZE);

  if (!rows.length) {
    return (
      <p className="text-sm text-gray-400 text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
        No members match this search.
      </p>
    );
  }

  const baseColSpan = 8 + (showTicketsColumn ? TICKET_STATUS_COLUMNS.length + 1 : 0);

  return (
    <>
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Agent</th>
            <th className="px-3 py-2.5 text-left font-semibold text-gray-500">Role</th>
            <th className="px-3 py-2.5 text-center font-semibold text-gray-500">Users responded</th>
            {showTicketsColumn && (
              <>
                <th className="px-3 py-2.5 text-center font-semibold text-gray-500">Total</th>
                {TICKET_STATUS_COLUMNS.map((col) => (
                  <th key={col.key} className="px-3 py-2.5 text-center font-semibold text-gray-500 whitespace-nowrap">
                    {col.label}
                  </th>
                ))}
              </>
            )}
            <th className="px-3 py-2.5 text-center font-semibold text-gray-500">Calls</th>
            <th className="px-3 py-2.5 text-center font-semibold text-gray-500">Users messaged</th>
            <th className="px-3 py-2.5 text-center font-semibold text-gray-500">Call min</th>
            <th className="px-3 py-2.5 text-center font-semibold text-gray-500">Clients</th>
            <th className="px-3 py-2.5 text-center font-semibold text-gray-500">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map((r) => {
            const agentKey = r.agentId || r.agent_id;
            const isExpanded = expandedAgentId === agentKey;
            const clientCalls = r.clientCalls || [];
            const counts = r.ticketCounts || {};
            const rowShowsTickets = isChatSupportTeamType(teamType || r.teamType, r.panelRole);
            return (
              <Fragment key={agentKey}>
                <tr className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-3 py-2.5">
                    <p className="font-medium text-gray-900">
                      {r.name || `${r.firstName || ""} ${r.lastName || ""}`.trim() || "—"}
                    </p>
                    <p className="text-xs text-gray-400">{r.email}</p>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex flex-wrap items-center gap-1">
                      <RoleBadge role={r.panelRole} />
                      {r.isTeamLead && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-indigo-50 text-indigo-700">
                          Lead
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-center font-semibold text-gray-800">
                    {r.support_users_responded || 0}
                  </td>
                  {showTicketsColumn && (
                    <>
                      <td className="px-3 py-2.5 text-center font-bold text-gray-900">
                        {rowShowsTickets ? counts.total || 0 : "—"}
                      </td>
                      {TICKET_STATUS_COLUMNS.map((col) => (
                        <td key={col.key} className="px-3 py-2.5 text-center text-gray-700">
                          {rowShowsTickets ? counts[col.key] || 0 : "—"}
                        </td>
                      ))}
                    </>
                  )}
                  <td className="px-3 py-2.5 text-center">{r.calls_count || 0}</td>
                  <td className="px-3 py-2.5 text-center">{r.messages_count || 0}</td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      type="button"
                      onClick={() => onToggleExpand?.(agentKey)}
                      className="font-semibold text-blue-700 hover:text-blue-900 hover:underline"
                      title="Show call time by client"
                    >
                      {callMinutes(r.calls_duration_sec)}
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-center text-gray-600">{clientCalls.length}</td>
                  <td className="px-3 py-2.5 text-center font-semibold text-emerald-700">
                    {r.sales_revenue || r.subscription_revenue || 0}
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="border-t border-gray-100 bg-slate-50/80">
                    <td colSpan={baseColSpan} className="px-4 py-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2">
                        Call time by client — {r.name || r.email}
                      </p>
                      <ClientCallSummaryTable rows={clientCalls} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
    <PaginationBar
      page={page}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      label="members"
      onPageChange={setPage}
    />
    </>
  );
}

export default function AgentReportsPage() {
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const canSearchAndBrowseTeams = [
    "Admin",
    "Developer",
    "CSR Team Lead",
    "Sales Team Lead",
  ].includes(userRole);

  const now = new Date();
  const [period, setPeriod] = useState("daily");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("all");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedAgentId, setExpandedAgentId] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [exportPeriod, setExportPeriod] = useState("daily");
  const [exportDate, setExportDate] = useState(todayKey());
  const [exportYear, setExportYear] = useState(now.getFullYear());
  const [exportMonth, setExportMonth] = useState(now.getMonth() + 1);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await agentReportsService.getSummary({
        period,
        ...(search ? { search } : {}),
      });
      setSummary(data);
    } catch {
      toast.error("Failed to load agent reports");
    } finally {
      setLoading(false);
    }
  }, [period, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setExpandedAgentId(null);
  }, [period, search, teamFilter]);

  const toggleAgentExpand = (agentId) => {
    setExpandedAgentId((prev) => (prev === agentId ? null : agentId));
  };

  const teams = summary?.teams || [];
  const teamOptions = useMemo(() => {
    return (summary?.teams || []).map((t) => ({
      id: t.teamId ? String(t.teamId) : "none",
      name: t.teamName || "No team",
    }));
  }, [summary?.teams]);

  const visibleTeams = useMemo(() => {
    if (teamFilter === "all") return teams;
    return teams.filter((t) => String(t.teamId || "none") === teamFilter);
  }, [teams, teamFilter]);

  const flatRows = useMemo(() => {
    if (teamFilter === "all") return summary?.performance || [];
    return visibleTeams.flatMap((t) => t.members || []);
  }, [summary?.performance, visibleTeams, teamFilter]);

  function openExportDialog() {
    setExportPeriod(period);
    setExportDate(todayKey());
    setExportYear(now.getFullYear());
    setExportMonth(now.getMonth() + 1);
    setShowExport(true);
  }

  async function confirmExportCsv() {
    setExporting(true);
    try {
      const params =
        exportPeriod === "monthly"
          ? { period: "monthly", year: exportYear, month: exportMonth }
          : {
              period: "daily",
              from: exportDate,
              to: exportDate,
              year: Number(exportDate.slice(0, 4)) || exportYear,
              month: Number(exportDate.slice(5, 7)) || exportMonth,
            };

      const data = await agentReportsService.getSummary({
        ...params,
        ...(search ? { search } : {}),
      });

      let rows = data?.performance || [];
      if (teamFilter !== "all") {
        const teamsList = data?.teams || [];
        rows = teamsList
          .filter((t) => String(t.teamId || "none") === teamFilter)
          .flatMap((t) => t.members || []);
      }

      if (!rows.length) {
        toast.error("No records for that period");
        return;
      }

      const headers = [
        "Period",
        "Team",
        "Agent",
        "Role",
        "Users responded",
        "Tickets handled",
        "Calls",
        "Messages",
        "Call minutes",
        "Sales revenue",
      ];
      const label =
        exportPeriod === "monthly"
          ? `${exportYear}-${String(exportMonth).padStart(2, "0")}`
          : exportDate;
      const csvRows = rows.map((r) => [
        exportPeriod,
        r.teamName || "",
        r.name || `${r.firstName || ""} ${r.lastName || ""}`.trim(),
        r.panelRole || "",
        r.support_users_responded ?? 0,
        r.support_tickets_handled ?? r.ticketCounts?.total ?? 0,
        r.calls_count ?? 0,
        r.messages_count ?? 0,
        callMinutes(r.calls_duration_sec),
        r.sales_revenue ?? 0,
      ]);
      downloadCsv(`agent-reports-${exportPeriod}-${label}.csv`, headers, csvRows);
      toast.success(
        `${exportPeriod === "daily" ? "Daily" : "Monthly"} CSV ready for ${label}`
      );
      setShowExport(false);
    } catch {
      toast.error("Could not export CSV");
    } finally {
      setExporting(false);
    }
  }

  if (!ALLOWED.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="p-4 w-full">
      {showExport ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-gray-200">
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-black text-gray-900">Download agent reports</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Choose daily or monthly, pick the date or month, then download.
                </p>
              </div>
              <button
                type="button"
                onClick={() => !exporting && setShowExport(false)}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"
                aria-label="Close"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-600">Report type</label>
                <div className="mt-1 flex gap-2">
                  {["daily", "monthly"].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setExportPeriod(p)}
                      className={`rounded-full px-4 py-2 text-sm font-semibold border ${
                        exportPeriod === p
                          ? "text-white border-transparent"
                          : "bg-white text-gray-700 border-gray-200"
                      }`}
                      style={
                        exportPeriod === p
                          ? { backgroundColor: THEME.primary }
                          : undefined
                      }
                    >
                      {p === "daily" ? "Daily" : "Monthly"}
                    </button>
                  ))}
                </div>
              </div>
              {exportPeriod === "daily" ? (
                <div>
                  <label className="text-xs font-semibold text-gray-600">Date</label>
                  <input
                    type="date"
                    className="mt-1 w-full border rounded-xl px-3 py-2.5 text-sm"
                    value={exportDate}
                    onChange={(e) => setExportDate(e.target.value)}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Year</label>
                    <input
                      type="number"
                      className="mt-1 w-full border rounded-xl px-3 py-2.5 text-sm"
                      value={exportYear}
                      onChange={(e) =>
                        setExportYear(Number(e.target.value) || now.getFullYear())
                      }
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Month</label>
                    <select
                      className="mt-1 w-full border rounded-xl px-3 py-2.5 text-sm"
                      value={exportMonth}
                      onChange={(e) => setExportMonth(Number(e.target.value))}
                    >
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                        <option key={m} value={m}>
                          {new Date(2000, m - 1, 1).toLocaleString("en", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 pb-5 flex justify-end gap-2">
              <button
                type="button"
                disabled={exporting}
                onClick={() => setShowExport(false)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={exporting}
                onClick={confirmExportCsv}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                <FiDownload />
                {exporting ? "Preparing…" : "Download CSV"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)] space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FiTrendingUp style={{ color: THEME.primary }} /> Agent Reports
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Daily and monthly performance for CSR, Chat Support, and sales. Pick a date
              or month before downloading CSV.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={openExportDialog}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ backgroundColor: THEME.primary }}
            >
              <FiDownload /> Download CSV
            </button>
            <button
              type="button"
              onClick={() => setPeriod(period === "daily" ? "monthly" : "daily")}
              className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100 hover:bg-gray-200"
            >
              {period === "daily" ? "Show monthly" : "Show daily"}
            </button>
          </div>
        </div>

        {canSearchAndBrowseTeams && (
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[220px]">
              <label className="block text-xs font-semibold text-gray-500 mb-1">
                Search member
              </label>
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name, email, or role…"
                  className={`${inputCls} w-full pl-9`}
                />
              </div>
            </div>
            <div className="min-w-[200px]">
              <label className="block text-xs font-semibold text-gray-500 mb-1">Team</label>
              <select
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                className={`${inputCls} w-full`}
              >
                <option value="all">All teams</option>
                {teamOptions.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-5">
            <CardsSkeleton count={3} cols="grid-cols-1 sm:grid-cols-3" />
            <TableSkeleton rows={8} cols={7} />
          </div>
        ) : (
          <div className="space-y-5">
            {canSearchAndBrowseTeams && visibleTeams.length > 0 ? (
              visibleTeams.map((team) => {
                const teamLead = (team.members || []).find((m) => m.isTeamLead);
                return (
                <section
                  key={team.teamId || team.teamName || "none"}
                  className="rounded-2xl border border-gray-200 overflow-hidden"
                >
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <FiUsers style={{ color: THEME.primary }} />
                      <div>
                        <h2 className="font-semibold text-gray-900">{team.teamName || "No team"}</h2>
                        <p className="text-xs text-gray-500">
                          {team.members?.length || 0} member
                          {(team.members?.length || 0) === 1 ? "" : "s"}
                          {teamLead ? (
                            <>
                              {" "}
                              · Team lead:{" "}
                              <span className="font-semibold text-indigo-700">
                                {teamLead.name ||
                                  `${teamLead.firstName || ""} ${teamLead.lastName || ""}`.trim()}
                              </span>
                            </>
                          ) : null}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                      <span>
                        Users:{" "}
                        <strong className="text-gray-900">
                          {team.totals?.support_users_responded || 0}
                        </strong>
                      </span>
                      {team.teamType === "chat_support" && (
                        <span>
                          Tickets:{" "}
                          <strong className="text-gray-900">
                            {team.totals?.support_tickets_handled || 0}
                          </strong>
                        </span>
                      )}
                      <span>
                        Calls:{" "}
                        <strong className="text-gray-900">{team.totals?.calls_count || 0}</strong>
                      </span>
                      <span>
                        Users messaged:{" "}
                        <strong className="text-gray-900">{team.totals?.messages_count || 0}</strong>
                      </span>
                      <span>
                        Call min:{" "}
                        <strong className="text-gray-900">
                          {callMinutes(team.totals?.calls_duration_sec)}
                        </strong>
                      </span>
                    </div>
                  </div>
                  <div className="p-3">
                    <PerformanceTable
                      rows={team.members || []}
                      teamType={team.teamType}
                      expandedAgentId={expandedAgentId}
                      onToggleExpand={toggleAgentExpand}
                    />
                  </div>
                </section>
                );
              })
            ) : (
              <PerformanceTable
                rows={flatRows}
                expandedAgentId={expandedAgentId}
                onToggleExpand={toggleAgentExpand}
              />
            )}

            {!loading && !(summary?.performance || []).length && (
              <p className="text-center text-gray-400 py-8">
                {search
                  ? "No members found for that search."
                  : "No performance data for this period yet."}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
